import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { Button, Field, Notice, Select } from "../components";
import { Link, useRouter } from "../router";
import { NeedBranch } from "../shell";
import { ID_LABEL, REASON_LABEL, SEX_LABEL } from "./patientLabels";

interface Candidate { id: string; uhid: string; fullName: string; sex: string; age: string; mobile: string | null; level: string; reasons: string[] }
const BLOOD = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const RELATIONS = ["mother", "father", "spouse", "son", "daughter", "brother", "sister", "grandparent", "guardian", "friend", "other"];
let STATES: { code: string; name: string }[] | null = null;

const empty = { fullName: "", sex: "", dateOfBirth: "", ageYears: "", ageMonths: "", useAge: false, mobile: "", email: "", alternateMobile: "",
  bloodGroup: "", line1: "", city: "", stateCode: "", pin: "", abhaNumber: "", abhaAddress: "", occupation: "",
  guardianName: "", guardianRelation: "", guardianMobile: "", emergencyName: "", emergencyRelation: "", emergencyMobile: "",
  idKind: "", idValue: "" };
type F = typeof empty;

function isMinor(f: F): boolean {
  if (f.useAge) return f.ageYears !== "" && Number(f.ageYears) < 18;
  if (!f.dateOfBirth) return false;
  const d = new Date(f.dateOfBirth), n = new Date();
  const age = n.getFullYear() - d.getFullYear() - (n < new Date(n.getFullYear(), d.getMonth(), d.getDate()) ? 1 : 0);
  return age < 18;
}

export function PatientForm({ patientId }: { patientId?: string }) {
  const { navigate } = useRouter();
  const [f, setF] = useState<F>(empty);
  const [version, setVersion] = useState<number | null>(null);
  const [states, setStates] = useState(STATES);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [dups, setDups] = useState<{ candidates: Candidate[]; reasonRequired: boolean } | null>(null);
  const [abhaHolder, setAbhaHolder] = useState<Candidate | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [more, setMore] = useState(false);

  useEffect(() => { if (!STATES) api.get("/api/v1/signup/reference").then((r) => { STATES = r.states; setStates(r.states); }).catch(() => {}); }, []);
  useEffect(() => {
    if (!patientId) return;
    api.authed(`/api/v1/patients/${patientId}`).then(({ patient: p }) => {
      // Editing needs the full identifiers back, so ask for the ones that exist (each reveal is audited).
      setVersion(p.version); setMore(true);
      setF({ ...empty, fullName: p.fullName, sex: p.sex, dateOfBirth: p.dobEstimated ? "" : p.dateOfBirth ?? "", useAge: p.dobEstimated,
        ageYears: p.dobEstimated && p.dateOfBirth ? String(new Date().getFullYear() - Number(p.dateOfBirth.slice(0, 4))) : "",
        bloodGroup: p.bloodGroup ?? "", line1: p.address.line1 ?? "", city: p.address.city ?? "", stateCode: p.address.stateCode ?? "", pin: p.address.pin ?? "",
        occupation: p.occupation ?? "", guardianName: p.guardian?.name ?? "", guardianRelation: p.guardian?.relation ?? "",
        emergencyName: p.emergencyContact?.name ?? "", emergencyRelation: p.emergencyContact?.relation ?? "" });
      const reveal = async (field: string, key: keyof F) => { try { const r = await api.authed(`/api/v1/patients/${patientId}/reveal`, { body: { field } }); setF((x) => ({ ...x, [key]: r.value ?? "" })); } catch { /* no permission: leave blank */ } };
      if (p.mobile) reveal("mobile", "mobile"); if (p.email) reveal("email", "email"); if (p.alternateMobile) reveal("alternateMobile", "alternateMobile");
      if (p.abhaNumber) reveal("abhaNumber", "abhaNumber"); if (p.abhaAddress) reveal("abhaAddress", "abhaAddress");
      if (p.guardian?.mobile) reveal("guardianMobile", "guardianMobile"); if (p.emergencyContact?.mobile) reveal("emergencyMobile", "emergencyMobile");
    }).catch((e) => setFormError(e.message));
  }, [patientId]);

  const minor = useMemo(() => isMinor(f), [f]);
  // Any edit dismisses a duplicate warning (the next submit re-checks), so corrected details can be resubmitted normally.
  const set = (k: keyof F) => (e: { target: { value: string } }) => { const v = e.target.value; setF((x) => ({ ...x, [k]: v })); setDups(null); setAbhaHolder(null);
    setErrors((er) => { const n = { ...er }; for (const key of Object.keys(n)) if (key === k || key.endsWith("." + k) || key.toLowerCase().includes(k.toLowerCase())) delete n[key]; return n; }); };
  const err = (k: string) => errors[k];

  const payload = () => ({
    fullName: f.fullName, sex: f.sex,
    ...(f.useAge ? { dateOfBirth: null, ageYears: f.ageYears === "" ? null : Number(f.ageYears), ageMonths: f.ageMonths === "" ? null : Number(f.ageMonths) } : { dateOfBirth: f.dateOfBirth || null }),
    mobile: f.mobile || null, email: f.email || null, alternateMobile: f.alternateMobile || null, bloodGroup: f.bloodGroup || null, occupation: f.occupation || null,
    address: { line1: f.line1 || null, city: f.city || null, stateCode: f.stateCode || null, pin: f.pin || null },
    abhaNumber: f.abhaNumber || null, abhaAddress: f.abhaAddress || null,
    guardian: f.guardianName || f.guardianMobile ? { name: f.guardianName, relation: f.guardianRelation, mobile: f.guardianMobile || null } : null,
    emergencyContact: f.emergencyName || f.emergencyMobile ? { name: f.emergencyName, relation: f.emergencyRelation, mobile: f.emergencyMobile || null } : null,
    // On edit, documents are not part of this form, so they are left out (the server keeps them).
    ...(patientId ? {} : { identifiers: f.idKind && f.idValue ? [{ kind: f.idKind, value: f.idValue }] : [] }),
  });

  async function submit(e: FormEvent | null, extra: { acknowledgeDuplicates?: boolean; overrideReason?: string } = {}) {
    e?.preventDefault(); setFormError(null); setAbhaHolder(null); setBusy(true);
    try {
      const r = patientId
        ? await api.authed(`/api/v1/patients/${patientId}`, { method: "PUT", body: { patient: payload(), version } })
        : await api.authed("/api/v1/patients", { body: { patient: payload(), ...extra } });
      navigate(`/app/patients/${r.patient.id}`, !!patientId);
    } catch (ex) {
      const a = ex as ApiError;
      if (a.code === "validation_failed") {
        setErrors(a.data.errors ?? {}); setDups(null);
        const moreKeys = Object.keys(a.data.errors ?? {}).filter((k) => !["fullName", "sex", "dateOfBirth", "ageYears", "mobile"].includes(k));
        if (moreKeys.length) setMore(true);
        setTimeout(() => (document.querySelector("[aria-invalid=true]") as HTMLElement | null)?.focus(), 0);
      } else if (a.code === "possible_duplicates") { setDups({ candidates: a.data.candidates, reasonRequired: a.data.reasonRequired }); setTimeout(() => document.getElementById("dup-panel")?.focus(), 0); }
      else if (a.code === "abha_in_use") setAbhaHolder(a.data.patient);
      else setFormError(a.message);
    } finally { setBusy(false); }
  }

  return (
    <NeedBranch>
      <h1>{patientId ? "Edit patient" : "Register patient"}</h1>
      <form onSubmit={submit} noValidate className="patient-form">
        {formError && <Notice tone="error">{formError}</Notice>}
        <Field label="Full name" value={f.fullName} onChange={set("fullName")} error={err("fullName")} autoComplete="off" autoFocus />
        <fieldset className={`seg${err("sex") ? " has-error" : ""}`} aria-invalid={err("sex") ? true : undefined}>
          <legend>Sex</legend>
          {["female", "male", "other", "unknown"].map((s) => (
            <label key={s} className={f.sex === s ? "on" : ""}><input type="radio" name="sex" value={s} checked={f.sex === s} onChange={set("sex")} />{SEX_LABEL[s]}</label>
          ))}
        </fieldset>
        {err("sex") && <p className="error-text seg-error">{err("sex")}</p>}
        {!f.useAge ? (
          <div className="dob-row">
            <Field label="Date of birth" type="date" max={new Date().toISOString().slice(0, 10)} value={f.dateOfBirth} onChange={set("dateOfBirth")} error={err("dateOfBirth")} />
            <button type="button" className="link-button" onClick={() => setF((x) => ({ ...x, useAge: true, dateOfBirth: "" }))}>Date not known? Enter age</button>
          </div>
        ) : (
          <div className="dob-row">
            <div className="row">
              <Field label="Age (years)" inputMode="numeric" value={f.ageYears} onChange={set("ageYears")} error={err("ageYears") ?? err("dateOfBirth")} />
              <Field label="Months" optional inputMode="numeric" value={f.ageMonths} onChange={set("ageMonths")} />
            </div>
            <button type="button" className="link-button" onClick={() => setF((x) => ({ ...x, useAge: false, ageYears: "", ageMonths: "" }))}>Enter date of birth instead</button>
          </div>
        )}
        <Field label="Mobile number" optional type="tel" inputMode="tel" placeholder="98765 43210" value={f.mobile} onChange={set("mobile")} error={err("mobile")} autoComplete="off" />

        {(minor || err("guardian.name")) && (
          <fieldset className="group">
            <legend>Parent or guardian</legend>
            <p className="hint">Required for patients under 18.</p>
            <Field label="Name" value={f.guardianName} onChange={set("guardianName")} error={err("guardian.name")} />
            <div className="row">
              <Select label="Relationship" placeholder="Select" value={f.guardianRelation} onChange={set("guardianRelation")} error={err("guardian.relation")}
                options={RELATIONS.map((r) => ({ value: r, label: r[0]!.toUpperCase() + r.slice(1) }))} />
              <Field label="Mobile" optional type="tel" value={f.guardianMobile} onChange={set("guardianMobile")} error={err("guardian.mobile")} />
            </div>
          </fieldset>
        )}

        <details className="more" open={more} onToggle={(e) => setMore((e.target as HTMLDetailsElement).open)}>
          <summary>More details</summary>
          <div className="row">
            <Field label="Email" optional type="email" value={f.email} onChange={set("email")} error={err("email")} />
            <Field label="Alternate mobile" optional type="tel" value={f.alternateMobile} onChange={set("alternateMobile")} error={err("alternateMobile")} />
          </div>
          <div className="row">
            <Select label="Blood group" placeholder="Not known" value={f.bloodGroup} onChange={set("bloodGroup")} options={BLOOD.map((b) => ({ value: b, label: b }))} />
            <Field label="Occupation" optional value={f.occupation} onChange={set("occupation")} />
          </div>
          <Field label="Address" optional value={f.line1} onChange={set("line1")} />
          <div className="row">
            <Field label="City" optional value={f.city} onChange={set("city")} />
            <Field label="PIN code" optional inputMode="numeric" maxLength={6} value={f.pin} onChange={set("pin")} error={err("address.pin")} />
          </div>
          <Select label="State or union territory" placeholder="Select" value={f.stateCode} onChange={set("stateCode")} error={err("address.stateCode")}
            options={(states ?? []).map((s) => ({ value: s.code, label: s.name }))} />
          <div className="row">
            <Field label="ABHA number" optional hint="14 digits, from the patient's ABHA card" inputMode="numeric" value={f.abhaNumber} onChange={set("abhaNumber")} error={err("abhaNumber")} />
            <Field label="ABHA address" optional hint="Looks like name@abdm" value={f.abhaAddress} onChange={set("abhaAddress")} error={err("abhaAddress")} />
          </div>
          {!patientId && <div className="row">
            <Select label="ID document" placeholder="None" value={f.idKind} onChange={set("idKind")} options={Object.entries(ID_LABEL).map(([value, label]) => ({ value, label }))} />
            <Field label="Document number" optional value={f.idValue} onChange={set("idValue")} error={err("identifiers.0.value")} disabled={!f.idKind} />
          </div>}
          {!minor && !err("guardian.name") && <fieldset className="group">
            <legend>Emergency contact</legend>
            <div className="row">
              <Field label="Name" optional value={f.emergencyName} onChange={set("emergencyName")} error={err("emergencyContact.name")} />
              <Select label="Relationship" placeholder="Select" value={f.emergencyRelation} onChange={set("emergencyRelation")} error={err("emergencyContact.relation")}
                options={RELATIONS.map((r) => ({ value: r, label: r[0]!.toUpperCase() + r.slice(1) }))} />
            </div>
            <Field label="Mobile" optional type="tel" value={f.emergencyMobile} onChange={set("emergencyMobile")} error={err("emergencyContact.mobile")} />
          </fieldset>}
        </details>

        {abhaHolder && <Notice tone="error">This ABHA number already belongs to <Link to={`/app/patients/${abhaHolder.id}`}>{abhaHolder.fullName} ({abhaHolder.uhid})</Link>. Open that record instead.</Notice>}

        {dups && (
          <section className="dup-panel" id="dup-panel" tabIndex={-1} aria-labelledby="dup-h">
            <h2 id="dup-h">This patient may already be registered</h2>
            <ul className="dup-list">{dups.candidates.map((c) => (
              <li key={c.id}>
                <div><Link to={`/app/patients/${c.id}`}>{c.fullName}</Link> <span className="mono muted">{c.uhid}</span><br />
                  <span className="muted">{c.age}, {SEX_LABEL[c.sex]}{c.mobile ? `, ${c.mobile}` : ""}</span></div>
                <ul className="reasons">{c.reasons.map((r) => <li key={r} className={r.startsWith("different") ? "against" : ""}>{REASON_LABEL[r] ?? r}</li>)}</ul>
                <Link to={`/app/patients/${c.id}`} className="btn btn-secondary">Open this record</Link>
              </li>))}
            </ul>
            {dups.reasonRequired ? <>
              <Field label="Why is this a different person?" hint="Required when a record looks like the same patient. This is saved in the audit log."
                value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
              <Button type="button" variant="secondary" busy={busy} disabled={overrideReason.trim().length < 10} onClick={() => submit(null, { overrideReason })}>Register as a new patient</Button>
            </> : <Button type="button" variant="secondary" busy={busy} onClick={() => submit(null, { acknowledgeDuplicates: true })}>Not the same person: register as new</Button>}
            <p className="aside"><button type="button" className="link-button" onClick={() => setDups(null)}>Go back and change the details</button></p>
          </section>
        )}

        {!dups && <div className="form-actions">
          <Button type="submit" busy={busy}>{patientId ? "Save changes" : "Register patient"}</Button>
          <Link to={patientId ? `/app/patients/${patientId}` : "/app/patients"} className="btn btn-quiet">Cancel</Link>
        </div>}
      </form>
    </NeedBranch>
  );
}
